import SelectController from '@/common/components/SelectController';
import TextField from '@/common/components/TextField';
import { DatePicker } from '@/common/components/ui/datePicker';
import { useCities } from '@/common/hooks/queries/useCities';
import useDebounce from '@/common/hooks/useDebounce';
import { GeoTransformers } from '@/common/services/geoTransformers';
import { Mapper } from '@/common/services/mapper';
import { filterLabel } from '@/features/reports/constants/filterLabel.constant';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PermittedReportFilterData } from '../types/permittedReports.type';
import { useProducts } from '@/common/hooks/queries/useProducts';
import { ProductData } from '@/common/types/product.type';
import { reportsControlStyles } from '@/common/components/Select/styles';
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer';
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { CompanyBranchData } from '@/common/types/companyBranches.type';
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches';
import { useCompanyEquipments } from '@/common/hooks/queries/useCompanyEquipments';
import { EquipmentsTransformer } from '@/common/services/equipmentTransformer';

interface FilterDefaultFieldType {
  [key: string]: (props: {
    control: Control;
    errors: FieldErrors;
    filterName: string;
    value: string;
    setFilterParams: (mutationFn: (searchParams: any) => any) => void;
    filterProps: PermittedReportFilterData;
  }) => React.ReactNode;
}

export const defaultReportsFilterFields: FilterDefaultFieldType = {
  textField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <Controller
        control={control}
        name={filterName}
        render={({ field: { onChange, ...rest } }) => (
          <TextField
            {...rest}
            label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
            className="w-full"
            value={value}
            markAsRequired={filterProps.required}
            onChange={(event: any) => {
              onChange(event);
              setFilterParams((searchParams) => ({
                ...searchParams,
                q: { ...searchParams.q, [filterName]: event.target.value },
              }));
            }}
            errorMessage={errors[filterName]?.message as string | undefined || undefined}
          />
        )}
      />
    );
  },
  dateField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    return (
      <Controller
        control={control}
        name={filterName}
        render={({ field }) => (
          <DatePicker
            name={filterName}
            label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
            date={!!value ? new Date(value) : undefined}
            setDate={(value) => {
              field.onChange(value);
              setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: value } }));
            }}
            markAsRequired={filterProps.required}
            variant="default"
            className="w-full"
            inputClassName="h-[52px] text-sm rounded-md rounded-r-none bg-neutral-op-8 hover:bg-neutral-op-8"
            errorMessage={errors[filterName]?.message as string | undefined || undefined}
          />
        )}
      />
    );
  },
  citySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCity, setSearchCity] = useDebounce('');

    const { data: citiesOptions, isFetching: citiesIsFetching } = useCities({
      filters: {
        search: searchCity,
      },
      mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCity}
        options={citiesOptions}
        isLoading={citiesIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  productSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCity, setSearchCity] = useDebounce('');

    const { data: productsOptions, isFetching: productsIsFetching } = useProducts({
      filters: {
        search: searchCity,
      },
      mapper: Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCity}
        options={productsOptions}
        isLoading={productsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  establishmentCompanySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchEstablishmentCompanies, setSearchEstablishmentCompanies] = useDebounce('');

    const { data: establishmentCompaniesOptions, isFetching: establishmentIsFetching } = useEstablishmentCompanies({
      filters: { search: searchEstablishmentCompanies },
      mapper: Mapper.mapWithTransformer({
        transformer: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'id' })
      })
    })

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchEstablishmentCompanies}
        options={establishmentCompaniesOptions}
        isLoading={establishmentIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  clientCompanySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCompany, setSearchCompany] = useDebounce('');

    const { data: companiesOptions, isFetching: companyIsFetching } = useCompanies({
      filters: { search: searchCompany },
      mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCompany}
        options={companiesOptions as { value: string, label: string }[]}
        isLoading={companyIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  clientCompanyBranchSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const { filterParams } = useFilterParams();

    const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('');

    const { data: companiesOptions,
      isFetching: companyIsFetching,
    } = useAvailableCompanyBranches({
      companyId: filterParams?.q?.client_company_id_eq?.value,
      filters: {
        search: searchCompanyBranch,
      },
      mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCompanyBranch}
        options={companiesOptions as { value: string, label: string }[]}
        isLoading={companyIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
        placeholder={!filterParams?.q?.client_company_id_eq?.value ? 'Selecione uma Empresa Cliente' : 'Selecione'}
        isDisabled={!filterParams?.q?.client_company_id_eq?.value}
      />
    );
  },
  clientEquipmentSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const { filterParams } = useFilterParams();

    const [searchEquipment, setSearchEquipment] = useDebounce('');

    const { data: equipmentsOptions,
      isFetching: equipmentsIsFetching,
    } = useCompanyEquipments({
      filters: {
        search: searchEquipment,
        companyId: filterParams?.q?.client_company_id_eq?.value,
      },
      mapper: Mapper.mapWithTransformer({
        transformer: EquipmentsTransformer.optionsTransformer(),
      }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchEquipment}
        options={equipmentsOptions}
        isLoading={equipmentsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
        placeholder={!filterParams?.q?.client_company_id_eq?.value ? 'Selecione uma Empresa Cliente' : 'Selecione'}
        isDisabled={!filterParams?.q?.client_company_id_eq?.value}
      />
    );
  },
};
